import clsx from 'clsx';
import type { ReactNode } from 'react';
import WdsAlert from './shared/components/ui/Alert';
import WdsBadge from './shared/components/ui/Badge';
import WdsCard from './shared/components/ui/Card';
import WdsTable, { type Column as WdsColumn } from './shared/components/ui/Table';

/** Kartu WDS dengan judul dan aksi opsional. */
export function Card({
  title,
  actions,
  children,
  className,
}: {
  title?: string;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <WdsCard title={title} actions={actions} className={className}>
      {children}
    </WdsCard>
  );
}

/** Judul halaman (di dalam konten); header aplikasi ada di `AppShell`. */
export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">{title}</h1>
        {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
      </div>
      {actions}
    </div>
  );
}

type Tone = 'neutral' | 'success' | 'warning' | 'danger' | 'info';
const BADGE: Record<Tone, 'neutral' | 'success' | 'warning' | 'danger' | 'primary'> = {
  neutral: 'neutral',
  success: 'success',
  warning: 'warning',
  danger: 'danger',
  info: 'primary',
};
export function Badge({ tone = 'neutral', children }: { tone?: Tone; children: ReactNode }) {
  return (
    <WdsBadge variant={BADGE[tone]} size="sm">
      {children}
    </WdsBadge>
  );
}

export function Alert({
  tone = 'danger',
  children,
}: {
  tone?: 'danger' | 'success' | 'info';
  children: ReactNode;
}) {
  return <WdsAlert variant={tone} message={children} />;
}

export interface Column<T> {
  header: string;
  cell: (row: T) => ReactNode;
  className?: string;
}

/** Tabel WDS sederhana (tanpa paginasi; halaman yang butuh paginasi mengaturnya sendiri). */
export function DataTable<T extends { id: string }>({
  columns,
  rows,
  empty = 'Belum ada data',
}: {
  columns: Column<T>[];
  rows: T[];
  empty?: string;
}) {
  const cols: WdsColumn<T>[] = columns.map((c) => ({
    key: c.header,
    header: c.header,
    render: (r) => c.cell(r),
    className: c.className,
  }));
  return (
    <div className={clsx('w-full')}>
      <WdsTable
        columns={cols}
        data={rows}
        rowKey={(r) => r.id}
        totalRecords={rows.length}
        page={1}
        pageSize={Math.max(rows.length, 1)}
        onPageChange={() => undefined}
        onPageSizeChange={() => undefined}
        hidePagination
        hideTotalRecords
        hideActionColumn
        isEmptyState={rows.length === 0}
        emptyStateTitle={empty}
        emptyStateDescription=""
      />
    </div>
  );
}
